import { useMutation, useQueries, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '@/lib/api-client';

export type TaxCategoryView = {
  id: string;
  displayName: string;
  allowedRates: number[];
  defaultRate: number | null;
  currentRate: number | null;
  currentRateActiveSince: string | null;
};

export type TaxCategoriesResponse = {
  countryPackId: string;
  categories: TaxCategoryView[];
};

/**
 * Translated labels for the tax categories the bundled country packs declare
 * (VL-306), resolved as `t(TAX_CATEGORY_LABEL_KEY[id], displayName)`.
 *
 * A pack's `display_name` is authored once, in English; these keys give the
 * uk and lt catalogues something to resolve. The English catalogue row equals
 * every bundled pack's `display_name` for that id (asserted by
 * `admin-ui-data-translations.spec.ts`), so an English operator sees exactly
 * the pack's wording. A category not listed here (a future pack's) renders the
 * pack's own `display_name`, untranslated rather than wrong.
 */
export const TAX_CATEGORY_LABEL_KEY: Readonly<Record<string, string>> = {
  standard: 'countryPacks.taxCategory.standard',
  reduced_14: 'countryPacks.taxCategory.reduced14',
  reduced_12: 'countryPacks.taxCategory.reduced12',
  reduced_9: 'countryPacks.taxCategory.reduced9',
  reduced_7: 'countryPacks.taxCategory.reduced7',
  reduced_5: 'countryPacks.taxCategory.reduced5',
  zero: 'countryPacks.taxCategory.zero',
  excise: 'countryPacks.taxCategory.excise',
  excise_zero: 'countryPacks.taxCategory.exciseZero',
  untaxed: 'countryPacks.taxCategory.untaxed',
};

/** A category's operator-facing name in the active locale. */
export function taxCategoryLabel(
  t: (key: string, bakedEnglish?: string) => string,
  category: Pick<TaxCategoryView, 'id' | 'displayName'>,
): string {
  const key = Object.hasOwn(TAX_CATEGORY_LABEL_KEY, category.id)
    ? TAX_CATEGORY_LABEL_KEY[category.id]
    : undefined;
  return key === undefined ? category.displayName : t(key, category.displayName);
}

const KEY = (packId: string | undefined) => ['tax-rates', 'categories', packId ?? ''] as const;

export function useTaxCategories(packId: string | undefined) {
  return useQuery({
    queryKey: KEY(packId),
    enabled: typeof packId === 'string' && packId.length > 0,
    queryFn: async (): Promise<TaxCategoriesResponse> => {
      const res = await apiClient.get<TaxCategoriesResponse>('/tax-rates/categories', {
        params: { countryPackId: packId },
      });
      return res.data;
    },
  });
}

export function useSetTaxRate(packId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: { taxCategory: string; ratePercent: number }) => {
      await apiClient.put(`/tax-rates/${packId}/${input.taxCategory}`, {
        ratePercent: input.ratePercent,
      });
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY(packId) }),
  });
}

/**
 * Categories for SEVERAL enabled country packs at once (RL-147).
 *
 * A deployment may have more than one pack enabled, and the same category id
 * can carry a different rate in each. Reading one pack and presenting its rate
 * as the item's rate is a wrong number shown authoritatively, so the caller
 * needs them all.
 */
export function useTaxCategoriesForPacks(packIds: readonly string[]) {
  return useQueries({
    queries: packIds.map((packId) => ({
      queryKey: KEY(packId),
      queryFn: async (): Promise<TaxCategoriesResponse> => {
        const res = await apiClient.get<TaxCategoriesResponse>('/tax-rates/categories', {
          params: { countryPackId: packId },
        });
        return res.data;
      },
    })),
  });
}
